import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function StripeConnectReturn() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [details, setDetails] = useState(null);
  const isRefresh = searchParams.get("refresh") === "1";

  useEffect(() => {
    if (isRefresh) {
      // Le lien d'onboarding a expiré, il faut en regénérer un
      setStatus("refresh");
      return;
    }
    checkStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkStatus = async () => {
    try {
      const { data } = await api.get("/stripe-connect/status");
      setDetails(data);

      if (data.charges_enabled) {
        setStatus("success");
        await refreshUser();
      } else if (data.details_submitted) {
        setStatus("pending");
      } else {
        setStatus("incomplete");
      }
    } catch {
      setStatus("error");
    }
  };

  const handleRetry = async () => {
    setStatus("loading");
    try {
      const { data } = await api.post("/stripe-connect/refresh", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto" />
          <p className="text-slate-600">Vérification de votre compte Stripe...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4 max-w-md">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">Compte Stripe connecté !</h1>
          <p className="text-slate-500">
            Votre compte bancaire est maintenant relié. Les paiements de vos élèves arriveront directement sur votre compte.
          </p>
          <Button onClick={() => navigate("/coach/payments")} className="rounded-full">
            Voir mes paiements
          </Button>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4 max-w-md">
          <Loader2 className="w-16 h-16 text-orange-500 mx-auto" />
          <h1 className="font-heading text-2xl font-bold text-slate-900">Vérification en cours</h1>
          <p className="text-slate-500">
            Vos informations ont été soumises. Stripe les vérifie, cela peut prendre quelques minutes.
            Vous serez informé une fois le compte activé.
          </p>
          <Button onClick={() => navigate("/coach/payments")} variant="outline" className="rounded-full">
            Retour aux paiements
          </Button>
        </div>
      </div>
    );
  }

  // refresh ou incomplete
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center space-y-4 max-w-md">
        <AlertTriangle className="w-16 h-16 text-orange-400 mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {status === "refresh" ? "Lien expiré" : "Inscription incomplète"}
        </h1>
        <p className="text-slate-500">
          {status === "refresh"
            ? "Le lien d'inscription Stripe a expiré. Cliquez ci-dessous pour en obtenir un nouveau."
            : "Votre inscription Stripe n'est pas complète. Veuillez la finaliser pour recevoir des paiements."}
        </p>
        <Button onClick={handleRetry} className="rounded-full">
          Reprendre l'inscription
        </Button>
      </div>
    </div>
  );
}
